import {
  List,
  Stack,
  Text,
  TextLink,
  useResponsiveValue,
} from 'braid-design-system';
import { GlobalNotificationBannerSkeleton } from '../GlobalNotificationBannerSkeleton/GlobalNotificationBannerSkeleton';
import { parse as parseCookies } from 'cookie';
import { selectPrivacyPolicyBanner } from 'src/store/selectors';
import { useSelector } from 'src/store/react';
import { useTranslations } from '@vocab/react';
import translations from './.vocab';
import { isPrivacyPolicyBannerFieldValid } from '../utils/isPrivacyPolicyBannerFieldValid';

const usePrivacyPolicyBanner = () => {
  const privacyPolicyBanner = useSelector(selectPrivacyPolicyBanner);
  const isPrivacyPolicyBannerOverride =
    typeof document !== 'undefined' &&
    parseCookies(document.cookie)?.PRIVACY_POLICY_BANNER_OVERRIDE === 'true';

  return {
    showCookieConsent:
      isPrivacyPolicyBannerFieldValid(privacyPolicyBanner?.cookieConsent) ||
      isPrivacyPolicyBannerOverride,
    showPrivacyPolicy:
      isPrivacyPolicyBannerFieldValid(privacyPolicyBanner?.privatePolicy) ||
      isPrivacyPolicyBannerOverride,
  };
};

export const PrivacyPolicyBanner = () => {
  const { t } = useTranslations(translations);
  const { showCookieConsent, showPrivacyPolicy } = usePrivacyPolicyBanner();
  const isMobile = useResponsiveValue()({
    mobile: true,
    tablet: false,
  });

  return showCookieConsent || showPrivacyPolicy ? (
    <GlobalNotificationBannerSkeleton
      dataAutomation="privacyPolicyBanner"
      tone="info"
      localStorageKey="pPB"
    >
      <Stack space="small">
        {showCookieConsent ? (
          <div data-automation="cookieConsent">
            <Stack space="small">
              <>
                {t('By continuing to use our platform...', {
                  Text: (children) =>
                    children?.length !== 0 ? (
                      <Text size="small">{children}</Text>
                    ) : null,
                  List: (children: any[]) =>
                    children?.length !== 0 ? (
                      <List space="xsmall">
                        {children.map((textList) => textList)}
                      </List>
                    ) : null,
                  TextList: (children) => <Text size="small">{children}</Text>,
                })}
              </>
            </Stack>
          </div>
        ) : null}
        {showPrivacyPolicy ? (
          <Text size="small" data={{ automation: 'privacyPolicyUpdate' }}>
            {isMobile
              ? t('We have updated our Privacy Policy.', {
                  TextLink: (children) => (
                    <TextLink href="/privacy">{children}</TextLink>
                  ),
                })
              : t('We have updated our Privacy Policy across...', {
                  TextLink: (children) => (
                    <TextLink href="/privacy">{children}</TextLink>
                  ),
                })}
          </Text>
        ) : null}
      </Stack>
    </GlobalNotificationBannerSkeleton>
  ) : null;
};
